import React from "react";
import {
  Package, Truck, AlertTriangle, Mail, MessageSquare, Shield,
  CheckCircle2, RefreshCw, ArrowUpRight, Loader2, Database,
  DollarSign, Clock, Wifi, WifiOff, Pause, MessageCircle, Activity,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

const REFRESH_MS = 60_000; // 1 minute

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s siden`;
  if (diff < 3600) return `${Math.floor(diff / 60)} min siden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}t siden`;
  return `${Math.floor(diff / 86400)}d siden`;
}

export default function DriftDashboard({ onJumpToTab }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [resolving, setResolving] = React.useState(null);

  const load = React.useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await adminApi().get("/admin/dashboard/today");
      setData(r.data);
    } catch (e) {
      if (!silent) toast.error("Kunne ikke hente drift-status");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    const t = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const resolveSmtp = async (id) => {
    setResolving(id);
    try {
      await adminApi().post(`/admin/system-errors/smtp/${id}/resolve`);
      toast.success("Markeret som håndteret");
      await load(true);
    } catch (e) {
      toast.error("Kunne ikke markere som håndteret");
    } finally {
      setResolving(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="bg-white border border-brand p-12 text-center text-brand-text2" data-testid="drift-loading">
        <Loader2 size={20} className="animate-spin mx-auto mb-3 text-brand-red" />
        Indlæser drift-status…
      </div>
    );
  }
  if (!data) return null;

  const alerts = data.alerts || [];
  const critical = alerts.filter((a) => a.level === "critical");
  const warnings = alerts.filter((a) => a.level === "warning");

  return (
    <div className="space-y-5" data-testid="drift-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-bold text-2xl text-brand-dark">Drift i dag</h2>
          <p className="text-xs text-brand-text2 mt-1">
            Senest opdateret {timeAgo(data.generated_at)} · auto-refresh hvert minut
          </p>
        </div>
        <button
          onClick={() => load(false)}
          className="text-xs text-brand-dark border border-brand-dark hover:bg-brand-dark hover:text-white px-3 py-2 flex items-center gap-1 transition-colors"
          data-testid="drift-refresh"
        >
          <RefreshCw size={12} />
          Opdater
        </button>
      </div>

      {/* Critical alerts — red banner */}
      {critical.map((a, i) => (
        <div
          key={`crit-${i}`}
          className="bg-red-600 text-white px-5 py-4 flex items-center gap-3 animate-pulse-once"
          data-testid="drift-alert-critical"
        >
          <AlertTriangle size={22} className="flex-shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-base">Handling kræves NU</div>
            <div className="text-sm">{a.msg}</div>
          </div>
        </div>
      ))}

      {/* Warning alerts */}
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-400 px-5 py-3 space-y-1" data-testid="drift-alert-warning">
          {warnings.map((a, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-amber-900">
              <AlertTriangle size={14} className="flex-shrink-0" />
              {a.msg}
            </div>
          ))}
        </div>
      )}

      {/* 1) Dagens bookinger */}
      <Section icon={<Package size={18} />} title="Dagens bookinger" onJump={() => onJumpToTab?.("bookings")}>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Tile label="Nye i dag" value={data.bookings.new_today} accent="text-brand-dark" testid="tile-bookings-new" />
          <Tile label="Skal afhentes" value={data.bookings.to_pickup} accent="text-amber-700" testid="tile-bookings-pickup" />
          <Tile label="Undervejs" value={data.bookings.in_transit} accent="text-blue-700" testid="tile-bookings-transit" />
          <Tile label="Leveret i dag" value={data.bookings.delivered_today} accent="text-emerald-700" testid="tile-bookings-delivered" />
          <Tile label="Problemer" value={data.bookings.problematic} accent={data.bookings.problematic > 0 ? "text-red-700" : "text-brand-text2"} testid="tile-bookings-problematic" />
        </div>
      </Section>

      {/* 2) Chauffør-status */}
      <Section icon={<Truck size={18} />} title="Chauffører" onJump={() => onJumpToTab?.("drivers")}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <Tile
            label="Online nu (<5 min)"
            value={data.drivers.online}
            accent="text-emerald-700"
            icon={<Wifi size={14} className="text-emerald-700" />}
            testid="tile-drivers-online"
          />
          <Tile
            label="Offline"
            value={data.drivers.offline}
            accent="text-brand-text2"
            icon={<WifiOff size={14} className="text-brand-text2" />}
            testid="tile-drivers-offline"
          />
          <Tile label="Aktive chauffører" value={data.drivers.total_active} accent="text-brand-dark" testid="tile-drivers-total" />
          <Tile label="Aktive ruter" value={data.drivers.active_routes} accent="text-blue-700" testid="tile-drivers-routes" />
        </div>
        {data.drivers.recent_activity?.length > 0 && (
          <div className="bg-brand-bg2 border border-brand p-3">
            <div className="kicker mb-2">Seneste GPS-aktivitet</div>
            <div className="space-y-1.5">
              {data.drivers.recent_activity.map((d) => (
                <div key={d.driver_id} className="flex items-center justify-between text-xs" data-testid="recent-activity-row">
                  <span className="text-brand-dark font-medium">{d.name}</span>
                  <span className="text-brand-text2 font-mono">{timeAgo(d.updated_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>

      {/* 2b) Fase 1C — Live driver-ops status */}
      {data.driver_ops && (
        <Section icon={<Activity size={18} />} title="Chauffør-drift live" onJump={() => onJumpToTab?.("driver_problems")}>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile
              label="Pausede chauffører"
              value={data.driver_ops.paused_drivers}
              accent={data.driver_ops.paused_drivers > 0 ? "text-red-700" : "text-emerald-700"}
              icon={<Pause size={14} className={data.driver_ops.paused_drivers > 0 ? "text-red-700" : "text-emerald-700"} />}
              testid="tile-paused-drivers"
              onClick={() => onJumpToTab?.("driver_problems")}
            />
            <Tile
              label="Åbne problemer"
              value={data.driver_ops.open_problems}
              accent={data.driver_ops.open_problems > 0 ? "text-amber-700" : "text-emerald-700"}
              icon={<AlertTriangle size={14} className={data.driver_ops.open_problems > 0 ? "text-amber-700" : "text-emerald-700"} />}
              testid="tile-open-problems"
              onClick={() => onJumpToTab?.("driver_problems")}
            />
            <Tile
              label="Akut ubehandlet"
              value={data.driver_ops.severe_open_problems}
              accent={data.driver_ops.severe_open_problems > 0 ? "text-red-700" : "text-emerald-700"}
              icon={<AlertTriangle size={14} className={data.driver_ops.severe_open_problems > 0 ? "text-red-700" : "text-emerald-700"} />}
              testid="tile-severe-problems"
              onClick={() => onJumpToTab?.("driver_problems")}
            />
            <Tile
              label="Ulæste beskeder"
              value={data.driver_ops.unread_driver_messages}
              accent={data.driver_ops.unread_driver_messages > 0 ? "text-blue-700" : "text-emerald-700"}
              icon={<MessageCircle size={14} className={data.driver_ops.unread_driver_messages > 0 ? "text-blue-700" : "text-emerald-700"} />}
              testid="tile-unread-msgs"
              onClick={() => onJumpToTab?.("driver_chat")}
            />
          </div>
        </Section>
      )}

      {/* 3) Billy-status */}
      <Section icon={<DollarSign size={18} />} title="Billy-bogføring" onJump={() => onJumpToTab?.("billy")}>
        <div className="grid grid-cols-2 gap-3">
          <Tile
            label="Pending push"
            value={data.billy.pending}
            accent={data.billy.pending > 0 ? "text-amber-700" : "text-emerald-700"}
            testid="tile-billy-pending"
          />
          <Tile
            label="Fejlede pushes"
            value={data.billy.failed_count}
            accent={data.billy.failed_count > 0 ? "text-red-700" : "text-emerald-700"}
            testid="tile-billy-failed"
          />
        </div>
        {data.billy.failed_bookings?.length > 0 && (
          <div className="mt-3 bg-red-50 border border-red-300 p-3 text-xs">
            <div className="font-bold text-red-900 mb-2">Fejlede fakturaer ↓</div>
            {data.billy.failed_bookings.slice(0, 5).map((b) => (
              <div key={b.id} className="py-1 border-t border-red-200 first:border-t-0 flex justify-between">
                <span className="font-mono">{b.booking_number} — {b.customer_email}</span>
                <span className="text-red-700">{b.billy_push_error?.slice(0, 60)}</span>
              </div>
            ))}
            <div className="text-xs text-red-700 mt-2">Retry-knap kommer i næste opdatering (punkt 6).</div>
          </div>
        )}
      </Section>

      {/* 4) Mail/system-fejl */}
      <Section icon={<Mail size={18} />} title="Mail- og system-fejl (sidste 24 timer)">
        {(data.system_errors.smtp_failures?.length === 0 && data.system_errors.backup_failures?.length === 0) ? (
          <div className="bg-emerald-50 border border-emerald-300 p-3 text-sm text-emerald-900 flex items-center gap-2" data-testid="no-system-errors">
            <CheckCircle2 size={16} />
            Ingen fejl registreret de sidste 24 timer
          </div>
        ) : (
          <div className="space-y-2">
            {data.system_errors.smtp_failures?.map((err) => (
              <div key={err._id} className="bg-red-50 border border-red-300 p-3 flex items-start justify-between gap-3" data-testid="smtp-error-row">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-red-900">SMTP: {err.subject?.slice(0, 80)}</div>
                  <div className="text-xs text-brand-text2 mt-1">til {err.to_email} · {timeAgo(err.created_at)}</div>
                  <div className="text-xs font-mono text-red-700 mt-1 break-all">{err.error?.slice(0, 200)}</div>
                </div>
                <button
                  onClick={() => resolveSmtp(err._id)}
                  disabled={resolving === err._id}
                  className="text-xs border border-red-700 text-red-700 hover:bg-red-700 hover:text-white px-2 py-1 flex-shrink-0 transition-colors disabled:opacity-60"
                  data-testid={`smtp-resolve-${err._id}`}
                >
                  {resolving === err._id ? <Loader2 size={11} className="animate-spin" /> : "Håndteret"}
                </button>
              </div>
            ))}
            {data.system_errors.backup_failures?.map((err) => (
              <div key={err.id} className="bg-amber-50 border border-amber-300 p-3" data-testid="backup-error-row">
                <div className="text-sm font-bold text-amber-900 flex items-center gap-2">
                  <Database size={14} /> Backup fejlede {timeAgo(err.started_at)}
                </div>
                <div className="text-xs text-amber-800 mt-1 font-mono">{err.error?.slice(0, 200)}</div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* 5) Tickets */}
      <Section icon={<MessageSquare size={18} />} title="Kundeservice / tickets" onJump={() => onJumpToTab?.("tickets")}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <Tile label="Nye" value={data.tickets.new} accent="text-brand-red" testid="tile-tickets-new" />
          <Tile label="Under behandling" value={data.tickets.in_progress} accent="text-amber-700" testid="tile-tickets-progress" />
          <Tile label="Åbne klager" value={data.tickets.klager_open} accent={data.tickets.klager_open > 0 ? "text-red-700" : "text-emerald-700"} testid="tile-tickets-klager" />
          <Tile label="Over SLA" value={data.tickets.overdue?.length || 0} accent={data.tickets.overdue?.length ? "text-red-700" : "text-emerald-700"} testid="tile-tickets-overdue" />
        </div>
        {data.tickets.overdue?.length > 0 && (
          <div className="bg-red-50 border border-red-300 p-3">
            <div className="kicker text-red-900 mb-2 flex items-center gap-1">
              <Clock size={12} /> SLA OVERSKREDET
            </div>
            <div className="space-y-1.5">
              {data.tickets.overdue.map((t) => {
                const overdueBy = Math.floor((Date.now() - new Date(t.sla_due_at).getTime()) / 60000);
                return (
                  <div key={t.id} className="flex justify-between text-xs gap-3" data-testid="overdue-ticket-row">
                    <div className="min-w-0 flex-1">
                      <div className="font-bold text-brand-dark truncate">{t.subject}</div>
                      <div className="text-brand-text2 text-[10px] uppercase tracking-wider">{t.source} · {t.priority} · {t.inbox}</div>
                    </div>
                    <div className="text-red-700 font-mono font-bold whitespace-nowrap">+{overdueBy} min</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Section>

      {/* 6) Whistleblower */}
      <Section icon={<Shield size={18} />} title="Whistleblower" onJump={() => onJumpToTab?.("whistleblower")}>
        <div className="grid grid-cols-2 gap-3">
          <Tile label="Nye" value={data.whistleblower.new} accent={data.whistleblower.new > 0 ? "text-red-700" : "text-emerald-700"} testid="tile-wb-new" />
          <Tile label="Åbne sager" value={data.whistleblower.open} accent={data.whistleblower.open > 0 ? "text-amber-700" : "text-emerald-700"} testid="tile-wb-open" />
        </div>
      </Section>

      <style>{`
        @keyframes pulse-once {
          0%, 100% { background-color: #dc2626; }
          50% { background-color: #b91c1c; }
        }
        .animate-pulse-once { animation: pulse-once 2s ease-in-out 3; }
      `}</style>
    </div>
  );
}

function Section({ icon, title, onJump, children }) {
  return (
    <div className="bg-white border border-brand p-5" data-testid={`section-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-base text-brand-dark flex items-center gap-2">
          <span className="text-brand-red">{icon}</span>
          {title}
        </h3>
        {onJump && (
          <button
            onClick={onJump}
            className="text-xs text-brand-text2 hover:text-brand-red flex items-center gap-1"
          >
            Åbn fane <ArrowUpRight size={11} />
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Tile({ label, value, accent, icon, testid, onClick }) {
  const clickable = !!onClick;
  const Comp = clickable ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={`text-left bg-brand-bg2 border border-brand p-3 w-full ${clickable ? "hover:border-brand-dark cursor-pointer transition-colors" : ""}`}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <div className="kicker">{label}</div>
        {icon}
      </div>
      <div className={`mt-2 text-3xl font-display font-bold ${accent}`}>{value}</div>
    </Comp>
  );
}
